'use client';

import React, { useEffect, useMemo, useRef } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useTranslation } from '@/app/components/translations/Translation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { hasReviewsContent } from '@/app/common/reviews/reviewSource';
import { useOverlayContext } from '@/app/common/context/OverlayContext';
import customCss from '@/app/components/hotel-detail/css/customCss';
import CloseIcon from '@/app/components/search/components/Modal/CloseIcon';
import { modalCloseButton } from '@/app/components/search/components/Modal/closeButtonStyles';
import AtolBadge from '@/app/components/search/components/Title/AtolBadge';
import { useHasWhatsAround } from './EvoWhatsAround';
import { useEvoFacilities } from './useEvoFacilities';

export const MODAL_SECTION_IDS = {
  details: 'evo-modal-section-details',
  overview: 'evo-modal-section-overview',
  facilities: 'evo-modal-section-facilities',
  reviews: 'evo-modal-section-reviews',
  location: 'evo-modal-section-location',
  policies: 'evo-modal-section-policies',
};

// Desktop groups everything under a single "Details & Facilities" tab; mobile splits it into
// finer-grained sections (overview / facilities / location). The facilities and location tabs are
// dropped from the nav when the hotel has no such data — see navLinks below.
const DESKTOP_NAV_LINKS = [
  { id: MODAL_SECTION_IDS.details, labelKey: 'vacation.select.cc.hotel_info.tab_details' },
  { id: MODAL_SECTION_IDS.reviews, labelKey: 'vacation.select.cc.hotel_info.tab_reviews' },
  { id: MODAL_SECTION_IDS.policies, labelKey: 'vacation.select.cc.hotel_info.tab_policies' },
];

const MOBILE_NAV_LINKS = [
  { id: MODAL_SECTION_IDS.overview, labelKey: 'vacation.select.cc.hotel_info.tab_overview' },
  { id: MODAL_SECTION_IDS.facilities, labelKey: 'vacation.select.cc.hotel_info.tab_facilities' },
  { id: MODAL_SECTION_IDS.reviews, labelKey: 'vacation.select.cc.hotel_info.tab_reviews' },
  { id: MODAL_SECTION_IDS.location, labelKey: 'vacation.select.cc.hotel_info.tab_location' },
  { id: MODAL_SECTION_IDS.policies, labelKey: 'vacation.select.cc.hotel_info.tab_policies' },
];

const Wrapper = styled.div<{ $showShadow?: boolean }>`
  position: relative;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
  padding-top: ${({ theme }) => theme.kallisto.space.space150};

  /* Bottom separator, mirroring the EvoTripOverview footer: at rest an inset hairline
     (aligned to the header padding, no shadow); once content has scrolled beneath the
     sticky header it stretches full-bleed and gains a drop shadow. */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 16px;
    right: 16px;
    height: 1px;
    background-color: ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};

    @media ${customCss.breakpoints.sm} {
      left: 24px;
      right: 24px;
    }
  }

  ${({ $showShadow, theme }) =>
    $showShadow &&
    css`
      box-shadow: ${theme.kallisto.shadow.mediumDownShadow};

      &::after {
        left: 0;
        right: 0;
      }
    `}
`;

const HeaderRow = styled.div`
  /* No vertical padding: box-sizing is border-box, so any would shrink the content box below
     the 40px CloseButton and make it overflow the row. */
  height: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;

  @media ${customCss.breakpoints.sm} {
    padding: 0 8px;
  }
`;

// Invisible 40px slot mirroring the close button so the title stays optically centred.
const HeaderSpacer = styled.div`
  width: 40px;
  flex-shrink: 0;
`;

const HotelName = styled.h2`
  flex: 1;
  min-width: 0;
  margin: 0;
  ${({ theme }) => theme.kallisto.mixins.font('Title4')};
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseButton = styled.button`
  ${modalCloseButton}
`;

const TabBar = styled.nav`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;

  /* No bottom padding on mobile: the active tab's underline sits flush with the
     header's bottom hairline (the Wrapper separator), which is the intended look.
     On larger viewports a 1px gap keeps the underline just above the hairline. */
  padding: 0 16px 0;
  margin-top: ${({ theme }) => theme.kallisto.space.space150};

  @media ${customCss.breakpoints.sm} {
    min-height: 56px;
    padding: 0 24px 1px;
  }
`;

const Tabs = styled.div`
  display: flex;
  overflow-x: auto;
  scrollbar-width: none;
  gap: ${({ theme }) => theme.kallisto.space.space100};

  &::-webkit-scrollbar {
    display: none;
  }

  /* Only one tab is highlighted at a time. While a tab is being hovered, the selected
     tab drops its highlight (reverts to the resting style) so the selected and hover
     styles never appear together — the hover always wins. */
  &:has(a:hover) a:not(:hover) {
    color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
    border-bottom-color: transparent;
  }
`;

const NavLink = styled.a<{ $active?: boolean }>`
  position: relative;
  display: block;
  white-space: nowrap;
  padding: 8px 8px 4px;
  color: ${(p) =>
    p.$active
      ? p.theme.kallisto.color.text.interactive.textPrimaryDefault
      : p.theme.kallisto.color.text.static.textPrimary};
  text-decoration: none;
  ${({ theme }) => theme.kallisto.mixins.font('Body')};
  font-weight: ${({ theme }) => theme.kallisto.font.weight.medium};
  border-bottom: 4px solid
    ${(p) => (p.$active ? p.theme.kallisto.color.border.interactive.borderPrimaryDefault : 'transparent')};
  transition:
    color 200ms ease,
    border-bottom-color 200ms ease;

  &:hover {
    color: ${(p) => p.theme.kallisto.color.text.interactive.textPrimaryDefault};
    border-bottom-color: ${(p) => p.theme.kallisto.color.text.interactive.textPrimaryDefault};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
  }

  &:focus-visible::before {
    content: '';
    position: absolute;
    inset: 6px 4px;
    border-radius: 6px;
    outline: 2px solid ${(p) => p.theme.kallisto.color.text.static.textPrimary};
    outline-offset: 0;
    pointer-events: none;
  }

  @media ${customCss.breakpoints.sm} {
    padding: 16px 12px 12px;
  }
`;

const AtolBadgeWrap = styled.div`
  display: none;
  flex-shrink: 0;

  /* Only from lg (MPH-839): below that the modal shows the mobile tab set, and the design leaves
     that row to the tabs alone. The gate matches the one that picks the tab set (isMobile in
     EvoHeroInfoPanel is measured on lg too), so the badge appears exactly with the desktop tabs. */
  @media ${customCss.breakpoints.lg} {
    display: block;
  }

  /* Match the Figma spec (node 979:25517): "ATOL PROTECTED" as Caption Bold with no
     underline, sitting a 4px gap from the 40px badge icon. */
  button {
    ${({ theme }) => theme.kallisto.mixins.font('Caption')};
    font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
    text-transform: uppercase;
    text-decoration: none;
  }

  svg {
    width: 40px !important;
    height: 40px !important;
  }
`;

const AtolPopoverZIndexFix = createGlobalStyle`
  #atol-badge-modal,
  #abta-badge-modal {
    z-index: 1500 !important;
    pointer-events: auto !important;
  }
`;

type Props = {
  handleClose: () => void;
  activeSection: string;
  onSelectSection: (id: string) => void;
  isMobile?: boolean;
};

export function EvoHotelDetailsHeader({ handleClose, activeSection, onSelectSection, isMobile }: Props) {
  const { t } = useTranslation();
  const { data: staticData } = useFetchHotelStatic();
  const { atol } = useInitialParams();
  const { accommodationOnly } = useUrlSearchParams();
  const { localization } = usePageWrapperData();
  const { reviewsDisabled } = localization.configValuesApp;
  // Belt and braces over the atol perimeter itself (createHotelDetailData): every other consumer of the
  // flag pairs it with this check, and this header was the one place a Hotel Only search could have
  // painted an "ATOL protected" badge on a booking that carries no such protection (MPH-935).
  const showAtol = atol && !accommodationOnly;
  // Shadow appears once the modal body has been scrolled beneath the sticky header.
  const { headerShadow } = useOverlayContext();

  const tabsRef = useRef<HTMLDivElement>(null);

  // Which tabs actually have content — a hotel can lack facilities or any location data, and a
  // fallback reviews source (Expedia/Booking/unknown) shows the TrustYou score but nothing to
  // open here. The desktop tab set has no facilities/location entries, so those checks are inert there.
  const { categories: facilityCategories } = useEvoFacilities();
  const hasWhatsAround = useHasWhatsAround();
  const hasFacilities = facilityCategories.length > 0;
  const hasLocation = hasWhatsAround || !!staticData?.coordinates || !!staticData?.direction;
  const showReviewsTab = !reviewsDisabled && hasReviewsContent(staticData?.hotelOpinion);

  const navLinks = useMemo(() => {
    const links = isMobile ? MOBILE_NAV_LINKS : DESKTOP_NAV_LINKS;
    return links.filter(({ id }) => {
      if (id === MODAL_SECTION_IDS.reviews) return showReviewsTab;
      if (id === MODAL_SECTION_IDS.facilities) return hasFacilities;
      if (id === MODAL_SECTION_IDS.location) return hasLocation;
      return true;
    });
  }, [isMobile, showReviewsTab, hasFacilities, hasLocation]);

  // When the active tab changes, scroll the tab bar so that tab sits as close to the
  // horizontal centre as possible. scrollTo clamps to the scrollable range, so tabs
  // near the edges settle flush without overscroll, and it's a no-op when nothing overflows.
  useEffect(() => {
    const container = tabsRef.current;
    if (!container) return;

    const activeEl = container.querySelector<HTMLElement>(`[data-section-id="${activeSection}"]`);
    if (!activeEl) return;

    const containerRect = container.getBoundingClientRect();
    const activeRect = activeEl.getBoundingClientRect();
    const activeCentre = activeRect.left - containerRect.left + container.scrollLeft + activeRect.width / 2;

    container.scrollTo({ left: activeCentre - container.clientWidth / 2, behavior: 'smooth' });
  }, [activeSection, navLinks]);

  const handleSelect = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    onSelectSection(id);
  };

  if (!staticData) return null;

  return (
    <Wrapper $showShadow={headerShadow}>
      <HeaderRow>
        <HeaderSpacer aria-hidden="true" />
        <HotelName>{staticData.name}</HotelName>
        <CloseButton type="button" onClick={handleClose} aria-label={t('vacation.select.details.close')}>
          <CloseIcon />
        </CloseButton>
      </HeaderRow>
      <TabBar>
        <Tabs ref={tabsRef}>
          {navLinks.map(({ id, labelKey }) => {
            const isActive = activeSection === id;
            return (
              <NavLink
                key={id}
                href={`#${id}`}
                onClick={handleSelect(id)}
                $active={isActive}
                aria-current={isActive ? 'true' : undefined}
                data-section-id={id}
              >
                {t(labelKey)}
              </NavLink>
            );
          })}
        </Tabs>
        {showAtol && (
          <AtolBadgeWrap>
            <AtolPopoverZIndexFix />
            <AtolBadge />
          </AtolBadgeWrap>
        )}
      </TabBar>
    </Wrapper>
  );
}
